import { type CreateItineraryComponentRequest, type ItineraryComponentOut } from '@utazzo/shared';
import {
  UzButton,
  UzCombobox,
  type UzComboboxOption,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { usePickerOptions } from '@/components/pickers/usePickerOptions';
import * as activitiesApi from '@/features/activities/activities.api';
import * as hotelsApi from '@/features/hotels/hotels.api';
import * as mealServicesApi from '@/features/meal-services/meal-services.api';
import * as transfersApi from '@/features/transfers/transfers.api';
import * as visasApi from '@/features/visas/visas.api';

/**
 * Add / edit drawer for one itinerary component (B5.E). The agent picks a
 * catalog kind, searches the matching master (DB source), and the picked row
 * seeds the title + FK; cost / markup / refundability are editable. FLIGHT is
 * intentionally absent - it requires the TBO Air module (B1.5).
 */

/** UI kind choices map to a component kind + the FK the master populates. */
type CatalogKind = 'HOTEL' | 'ACTIVITY' | 'TRANSFER' | 'VISA' | 'MEAL';

interface CatalogConfig {
  label: string;
  componentKind: CreateItineraryComponentRequest['kind'];
  fkField: 'hotelId' | 'activityId' | 'transferId' | 'visaId' | 'mealServiceId';
  cacheKey: string;
  searchPlaceholder: string;
  list: (search: string) => Promise<Array<{ id: string; name: string }>>;
  one: (id: string) => Promise<{ id: string; name: string } | null>;
}

const page = (search: string) => ({
  page: 1,
  pageSize: 30,
  sortDir: 'asc' as const,
  ...(search ? { search } : {}),
});

const CATALOG: Record<CatalogKind, CatalogConfig> = {
  HOTEL: {
    label: 'Hotel',
    componentKind: 'HOTEL',
    fkField: 'hotelId',
    cacheKey: 'itin.picker.hotels',
    searchPlaceholder: 'Search hotels...',
    list: (s) => hotelsApi.listHotels(page(s)).then((r) => r.data),
    one: (id) => hotelsApi.getHotel(id).catch(() => null),
  },
  ACTIVITY: {
    label: 'Activity',
    componentKind: 'ACTIVITY',
    fkField: 'activityId',
    cacheKey: 'itin.picker.activities',
    searchPlaceholder: 'Search activities...',
    list: (s) => activitiesApi.listActivities(page(s)).then((r) => r.data),
    one: (id) => activitiesApi.getActivity(id).catch(() => null),
  },
  TRANSFER: {
    label: 'Transfer',
    componentKind: 'TRANSFER',
    fkField: 'transferId',
    cacheKey: 'itin.picker.transfers',
    searchPlaceholder: 'Search transfers...',
    list: (s) => transfersApi.listTransfers(page(s)).then((r) => r.data),
    one: (id) => transfersApi.getTransfer(id).catch(() => null),
  },
  VISA: {
    label: 'Visa',
    componentKind: 'VAS',
    fkField: 'visaId',
    cacheKey: 'itin.picker.visas',
    searchPlaceholder: 'Search visas...',
    list: (s) => visasApi.listVisas(page(s)).then((r) => r.data),
    one: (id) => visasApi.getVisa(id).catch(() => null),
  },
  MEAL: {
    label: 'Meal service',
    componentKind: 'VAS',
    fkField: 'mealServiceId',
    cacheKey: 'itin.picker.meals',
    searchPlaceholder: 'Search meal services...',
    list: (s) => mealServicesApi.listMealServices(page(s)).then((r) => r.data),
    one: (id) => mealServicesApi.getMealService(id).catch(() => null),
  },
};

const KIND_CHOICES: ReadonlyArray<{ value: CatalogKind; label: string }> = [
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'VISA', label: 'Visa' },
  { value: 'MEAL', label: 'Meal service' },
];

/** Derive the UI catalog kind from an existing component row. */
const catalogKindOf = (c: ItineraryComponentOut): CatalogKind => {
  if (c.kind === 'HOTEL') return 'HOTEL';
  if (c.kind === 'ACTIVITY') return 'ACTIVITY';
  if (c.kind === 'TRANSFER') return 'TRANSFER';
  if (c.visaId) return 'VISA';
  return 'MEAL';
};

const catalogValueOf = (c: ItineraryComponentOut, cfg: CatalogConfig): string | null => {
  const v = c[cfg.fkField];
  return typeof v === 'string' ? v : null;
};

const paiseToRupees = (paise: string | undefined): number | '' => {
  const n = Number(paise ?? '0');
  if (!Number.isFinite(n) || n === 0) return '';
  return n / 100;
};
const rupeesToPaise = (rupees: number | null): string =>
  rupees === null ? '0' : String(Math.round(rupees * 100));

interface ComponentFormValues {
  title: string;
  costPaise: string;
  markupPaise: string;
  isRefundable: boolean;
  descriptionHtml: string | null;
}

interface ComponentFormDrawerProps {
  mode: 'create' | 'edit';
  component?: ItineraryComponentOut;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateItineraryComponentRequest) => Promise<void>;
}

export const ComponentFormDrawer = ({
  mode,
  component,
  pending,
  onClose,
  onSubmit,
}: ComponentFormDrawerProps): React.JSX.Element => {
  const [catalogKind, setCatalogKind] = useState<CatalogKind>(
    component ? catalogKindOf(component) : 'HOTEL',
  );
  const cfg = CATALOG[catalogKind];
  const [catalogId, setCatalogId] = useState<string | null>(
    component ? catalogValueOf(component, cfg) : null,
  );

  const form = useForm<ComponentFormValues>({
    defaultValues: {
      title: component?.title ?? '',
      costPaise: component?.costPaise ?? '0',
      markupPaise: component?.markupPaise ?? '0',
      isRefundable: component?.isRefundable ?? true,
      descriptionHtml: component?.descriptionHtml ?? null,
    },
  });

  // When the kind changes on a fresh create, clear the picked catalog row.
  useEffect(() => {
    if (mode === 'create') setCatalogId(null);
  }, [catalogKind, mode]);

  const submit = form.handleSubmit(async (values) => {
    const payload: CreateItineraryComponentRequest = {
      kind: cfg.componentKind,
      source: 'DB',
      title: values.title.trim(),
      descriptionHtml: values.descriptionHtml?.trim() ? values.descriptionHtml.trim() : null,
      costPaise: values.costPaise || '0',
      markupPaise: values.markupPaise || '0',
      isRefundable: values.isRefundable,
      [cfg.fkField]: catalogId,
    };
    await onSubmit(payload);
  });

  const handlePick = (id: string | null, label: string | null): void => {
    setCatalogId(id);
    // Seed the title from the catalog row if the agent hasn't typed one.
    const current = form.getValues('title');
    if (label && (!current || current.trim() === '')) {
      form.setValue('title', label, { shouldDirty: true });
    }
  };

  return (
    <UzDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={mode === 'create' ? 'Add component' : 'Edit component'}
      description="Pick a catalog item, then set the cost the customer is quoted."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={submit} loading={pending} disabled={!catalogId}>
            {mode === 'create' ? 'Add component' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <div className="space-y-5">
        <UzSelect
          name="catalogKind"
          label="Type"
          options={KIND_CHOICES}
          value={catalogKind}
          onValueChange={(v) => setCatalogKind(v as CatalogKind)}
          disabled={pending || mode === 'edit'}
          hint={mode === 'edit' ? 'Type is fixed once a component is created.' : undefined}
        />

        <CatalogItemPicker
          key={catalogKind}
          cfg={cfg}
          value={catalogId}
          onPick={handlePick}
          disabled={pending}
        />

        <UzForm form={form} onSubmit={submit}>
          <div className="space-y-5">
            <UzFormField name="title">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | undefined) ?? ''}
                  label="Title"
                  placeholder="Shown on the day and the docket"
                  error={error}
                  required
                  disabled={pending}
                />
              )}
            </UzFormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <UzFormField name="costPaise">
                {({ field, error }) => (
                  <UzNumberInput
                    name={field.name}
                    label="Net cost"
                    precision={2}
                    min={0}
                    leftAdornment={<span className="text-sm text-zinc-500">₹</span>}
                    value={paiseToRupees(field.value as string | undefined)}
                    onValueChange={(v) => field.onChange(rupeesToPaise(v))}
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
              <UzFormField name="markupPaise">
                {({ field, error }) => (
                  <UzNumberInput
                    name={field.name}
                    label="Markup"
                    precision={2}
                    min={0}
                    leftAdornment={<span className="text-sm text-zinc-500">₹</span>}
                    value={paiseToRupees(field.value as string | undefined)}
                    onValueChange={(v) => field.onChange(rupeesToPaise(v))}
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
            </div>

            <UzFormField name="isRefundable">
              {({ field }) => (
                <UzSwitch
                  label="Refundable"
                  description="Whether this component can be cancelled for a refund."
                  checked={field.value !== false}
                  onCheckedChange={field.onChange}
                  disabled={pending}
                />
              )}
            </UzFormField>

            <UzFormField name="descriptionHtml">
              {({ field, error }) => (
                <UzTextarea
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  label="Notes"
                  placeholder="Optional detail shown under the title."
                  rows={3}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
        </UzForm>
      </div>
    </UzDrawer>
  );
};

interface CatalogItemPickerProps {
  cfg: CatalogConfig;
  value: string | null;
  onPick: (id: string | null, label: string | null) => void;
  disabled?: boolean;
}

const CatalogItemPicker = ({
  cfg,
  value,
  onPick,
  disabled,
}: CatalogItemPickerProps): React.JSX.Element => {
  const { options, loading, setSearch } = usePickerOptions<{ id: string; name: string }>(
    {
      cacheKey: cfg.cacheKey,
      fetch: cfg.list,
      fetchOne: cfg.one,
      toOption: (row): UzComboboxOption => ({ value: row.id, label: row.name }),
    },
    value,
  );

  const labelFor = useMemo(
    () =>
      (id: string | null): string | null =>
        id ? (options.find((o) => o.value === id)?.label ?? null) : null,
    [options],
  );

  return (
    <UzCombobox
      label={cfg.label}
      placeholder={`Select a ${cfg.label.toLowerCase()}`}
      searchPlaceholder={cfg.searchPlaceholder}
      value={value ?? ''}
      onValueChange={(v) => onPick(v || null, labelFor(v || null))}
      onSearchChange={setSearch}
      options={options}
      loading={loading}
      disabled={disabled}
    />
  );
};
